<template>
  <MainLayout
    v-scroll="onScroll"
    scroll-toolbar
    use-stable-scroll-gutter
    :bg-color="''"
  >
    <!-- Layout Toolbar -->
    <template #toolbar>
      <div v-if="!spyderPaywall.show"
        class="relative flex items-center justify-between px-5 py-3.5 border-b border-border-normal">
        <div class="flex items-center gap-3">
          <img src="../../assets/icons/spyder.png" class="w-8 h-8">
          <BaseText type="label" size="sm" class="text-text-muted">
            Spyder
          </BaseText>
        </div>

        <!-- Spyder Search Bar -->
        <div class="absolute flex top-1/2 transform -translate-y-1/2 pointer-events-none" style="left: 308px; right: 308px; z-index: 40001">
          <div class="relative flex-grow mx-auto min-w-0 h-8 pointer-events-auto" style="max-width: 600px">
            <!-- Mirrors SearchBar's inset and padding so the bar lines up exactly with the Tracked Brands tab -->
            <div class="absolute top-0 left-0 right-0 px-2" style="min-width: 280px">
              <div
                class="group flex items-center gap-1.5 w-full pl-1.5 py-1.5 pr-1 rounded-md search-container search-input-container"
                @mouseenter="searchBarHovered = true" @mouseleave="searchBarHovered = false">
                <DynamicSearchIcon class="flex-shrink-0" :is-hovered="searchBarHovered || isSearchFocussed" />
                <!-- Search Input -->
                <input v-model="textSearch" type="text" class="flex-grow min-w-0 search-input"
                  placeholder="Search ads..." @focus="isSearchFocussed = true"
                  @blur="isSearchFocussed = false"
                  @keyup.enter="fetchAdvertisements">
              </div>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-3">
          <button class="flex items-center gap-1.5
          pl-1 py-1 pr-3 rounded-full border border-border-normal"
            style="transition: background-color 150ms ease-in-out, border-color 150ms ease-in-out, opacity 100ms ease-in-out;"
            :class="[
              loadingAds ? 'opacity-0' : 'opacity-100',
              { 'cursor-default': isAnnualPlan }
            ]" :disabled="isAnnualPlan" @click="showBuySpyderCreditsModal = true">
            <img v-if="getSpyderSubscriptions.length > 0" src="../../assets/icons/spyder-token.png" class="w-5 h-5">
            <img v-else src="../../assets/icons/spyder-token-disabled.png" class="w-5 h-5">
            <BaseText type="label" size="sm" class="text-text-muted">
              {{ getSpyderSubscriptions?.length }}/{{ getSpyderLimit }} credits
            </BaseText>
          </button>
          <BaseButton primary icon-only @click="showAddSpyderModal = true">
            <PlusIcon stroke="white" />
          </BaseButton>
        </div>
      </div>
      <!-- Subview Tabs -->
      <div class="flex gap-2 w-full px-5 py-2 border-b border-border-normal">
        <router-link
          :to="{ name: 'SpyderBrandsView' }"
          class="spyder-view-tab"
          :class="{ 'active': selectedTab === 'brands' }"
        >
          <SpyderIcon class="w-5 h-5" />
          Tracked Brands
        </router-link>
        <router-link
          :to="{ name: 'SpyderFeedView' }"
          class="spyder-view-tab"
          :class="{ 'active': selectedTab === 'feed' }"
        >
          <CommunityFeedIcon class="w-5 h-5" />
          Feed
        </router-link>
      </div>
    </template>

    <!-- Filter Bar -->
    <template #filter>
      <div
        ref="filterBar"
        class="border-b border-l border-r hidden sm:block bg-white bg-opacity-90 border-border-normal"
        style="backdrop-filter: blur(6px)"
      >
        <FilterTopBar
          discovery
          no-search
          hide-saved-filters
          :active-filters.sync="activeFilters"
          :selected-date-range.sync="selectedDateRange"
          :sort-order.sync="sortOrder"
          :custom-filter-categories="folderFilterCategories"
          @change="fetchAdvertisements"
        />
      </div>
    </template>

    <!-- Layout Content -->
    <template #content>
      <!-- Loading State -->
      <BaseLoadingLogo v-if="loadingAds" class="mt-24" />

      <!-- Paywall State -->
      <SpyderPaywallState
        v-else-if="spyderPaywall.show"
        class="-mt-4"
        :subscriptionType="spyderPaywall"
      />

      <!-- Empty State - No Brands -->
      <div
        v-else-if="!getSpyderSubscriptions.length"
        class="flex flex-col items-center mt-20"
      >
        <img
          src="../../assets/images/empty-with-filters.gif"
          class="border border-gray-400 rounded mx-auto object-cover"
          style="width: 400px; height: 300px"
        >
        <div class="text-lg font-bold my-4">
          No tracked brands yet
        </div>
        <div class="text-center text-gray-700">
          Add brands to Spyder to see their ads in your feed
        </div>
        <div class="flex mt-5">
          <BaseButton primary @click="$router.push({ name: 'SpyderBrandsView' })">
            Add Brands
          </BaseButton>
        </div>
      </div>

      <!-- Empty State - No Ads -->
      <div
        v-else-if="!advertisements.length && !loadingAds"
        class="flex flex-col items-center mt-20"
      >
        <img
          src="../../assets/images/empty-with-filters.gif"
          class="border border-gray-400 rounded mx-auto object-cover"
          style="width: 400px; height: 300px"
        >
        <div class="text-lg font-bold my-4">
          No ads found
        </div>
        <div class="text-center text-gray-700">
          Try adjusting your filters or search
        </div>
        <div class="flex mt-5">
          <BaseButton primary @click="clearFilters">
            Clear filters
          </BaseButton>
        </div>
      </div>

      <!-- Feed Intro Callout + Advertisements List -->
      <template v-else>
        <!-- Feed Intro Callout -->
        <div
          v-if="showFeedCallout"
          class="feed-callout mb-4 p-4 rounded-xl flex items-start justify-between"
        >
          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-1.5">
              <CommunityFeedIcon class="w-4 h-4 text-text-muted" />
              <BaseText type="label" size="sm" class="text-text-muted">
                The Feed Tab in Spyder
              </BaseText>
            </div>
            <BaseText type="body" size="sm" class="text-text-normal">
              All your tracked brands, one powerful stream. See every new ad the moment it goes live, no more switching between brands. Filter this view by folders to instantly focus on specific brand groups, whether that's competitors, clients, or inspiration sources.
            </BaseText>
          </div>
          <button
            class="flex-shrink-0 p-1 rounded-md transition-colors duration-100 hover:bg-neutral-50"
            @click="dismissFeedCallout"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M4.5 4.5L11.5 11.5M11.5 4.5L4.5 11.5" stroke="#5E6678" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>

        <!-- Advertisements List -->
        <AdvertisementList
          :discovery="true"
          :advertisements="advertisements"
          :loaded="!loadingAds"
          @updateAds="updateAdsLocal"
          @getMoreAds="() => getMoreAds({})"
          @reload="fetchAdvertisements(false)"
        />
      </template>

      <infinite-loading
        v-if="advertisements.length"
        :identifier="infiniteId"
        @infinite="getMoreAds"
      >
        <div slot="spinner">
          <BaseLoadingLogo :margin="2" />
        </div>
        <div slot="no-more" />
        <div slot="no-results" />
      </infinite-loading>

      <BuySpyderCreditsModal
        v-if="showBuySpyderCreditsModal && !isAnnualPlan"
        :current-sub-count="getSpyderSubscriptions?.length"
        :spyder-limit="getSpyderLimit"
        @close="showBuySpyderCreditsModal = false"
      />
      <AddSpyderModal
        v-if="showAddSpyderModal"
        @close="showAddSpyderModal = false"
      />
    </template>
  </MainLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import InfiniteLoading from 'vue-infinite-loading'
import ForeplayAPI from '@/api/foreplayServer'
import { checkAndCacheAll, clearUniqueCreatives } from '@/db/UniqueCreativesDB'
import { getBrandsInFolder, getUnfolderedBrands } from '@/utils/spyder/brandFolderUtils'

// Components
import MainLayout from '../../layouts/MainLayout.vue'
import AdvertisementList from '../../components/advertisements/AdvertisementList.vue'
import FilterTopBar from '../../components/search/FilterTopBar.vue'
import SpyderPaywallState from '../../components/spyderV2/SpyderPaywallState.vue'
import BuySpyderCreditsModal from '../../components/spyderV2/BuySpyderCreditsModal.vue'
import AddSpyderModal from '../../components/spyderV2/AddSpyderModal.vue'

// Icons
import PlusIcon from '../../components/globals/Icons/PlusIcon.vue'
import DynamicSearchIcon from '../../components/globals/Icons/SearchIcons/DynamicSearchIcon.vue'
import SpyderIcon from '../../components/globals/Icons/SidebarIcons/SpyderIcon.vue'
import CommunityFeedIcon from '../../components/globals/Icons/DiscoveryIcons/CommunityFeedIcon.vue'
import FolderIcon from '../../components/globals/Icons/SpyderV2Icons/FolderIcon.vue'

// The folder filter is specific to this view, so it's defined here and passed into the filter bar
// as a custom category rather than living in the app-wide filter options.
const FOLDER_CATEGORY_ID = 'spyder_folders'
const FOLDER_FILTER_KEY = `or.${FOLDER_CATEGORY_ID}`

// Brands that aren't in any folder are grouped under a synthetic "Default Folder", which the rest
// of Spyder also represents as a folder with a null id (see SpyderBrandsView).
const DEFAULT_FOLDER_ID = null

export default {
  name: 'SpyderFeedView',
  components: {
    MainLayout,
    AdvertisementList,
    FilterTopBar,
    InfiniteLoading,
    SpyderPaywallState,
    BuySpyderCreditsModal,
    AddSpyderModal,
    PlusIcon,
    DynamicSearchIcon,
    SpyderIcon,
    CommunityFeedIcon
  },
  data () {
    return {
      advertisements: [],
      loadingAds: true,
      activeFilters: {},
      selectedDateRange: {
        start: null,
        end: null
      },
      sortOrder: [{ name: 'Newest', value: 'desc' }],
      textSearch: '',
      lastDocId: null,
      infiniteId: +new Date(),
      isSearchFocussed: false,
      searchBarHovered: false,
      showBuySpyderCreditsModal: false,
      showAddSpyderModal: false,
      showFeedCallout: false
    }
  },
  watch: {
    // Grouping toggled here or in Settings > Preferences
    getUniqueCreatives () {
      this.fetchAdvertisements()
    }
  },
  computed: {
    ...mapGetters('AuthModule', ['getUser', 'getTeam', 'getUserSubscription', 'getSpyderLimit', 'getIsLegacy']),
    ...mapGetters('SpyderModule', ['getSpyderSubscriptions', 'getSpyderSubscriptionsLoading']),
    ...mapGetters('BoardsModule', ['getFolders']),
    ...mapGetters('MiscModule', ['getUniqueCreatives']),
    selectedTab () {
      return 'feed'
    },
    isAnnualPlan () {
      return this.getUserSubscription?.interval === 'year'
    },
    spyderPaywall () {
      const userSubscription = this.getUserSubscription
      const isBasic = userSubscription?.internalProductId === 'basic_plan'
      const isLegacy = this.getIsLegacy
      return {
        show: ((isBasic && this.getSpyderLimit === 0) || (isLegacy && this.getSpyderLimit === 0) || this.getSpyderLimit === 0),
        isLegacy,
        isBasic
      }
    },
    // The view's own filter category, passed to the filter bar as a custom category. Only folders
    // that still hold at least one tracked brand are offered, the Default Folder included.
    folderFilterCategories () {
      const hasUnfolderedBrands = getUnfolderedBrands(this.getSpyderSubscriptions, this.getFolders).length > 0

      const options = [
        ...(hasUnfolderedBrands ? [{ id: DEFAULT_FOLDER_ID, name: 'Default Folder', icon: FolderIcon }] : []),
        ...this.getFolders
          .filter(folder => getBrandsInFolder(this.getSpyderSubscriptions, folder).length > 0)
          .map(folder => ({
            id: folder.id,
            name: folder.name,
            icon: FolderIcon
          }))
      ]

      if (!options.length) return []

      return [{
        id: FOLDER_CATEGORY_ID,
        name: 'Folder',
        products: ['spyder'],
        searchPlaceholder: 'Search Folders...',
        icon: FolderIcon,
        // No serverKey — the selection is resolved to brand ids client-side and passed to the
        // feed endpoint as its own argument, so it's stripped out of the filters handed to the
        // query builder. See selectedBrandIds/apiFilters below.
        optionValueKey: 'id',
        valueTransform: (value) => value,
        disableExclude: true,
        options
      }]
    },
    // Folder ids selected in the folder filter. Empty = every tracked brand.
    selectedFolderIds () {
      return this.activeFilters[FOLDER_FILTER_KEY] ?? []
    },
    // Filters minus the folder selection, which the feed endpoint takes as brand ids instead
    apiFilters () {
      const filters = { ...this.activeFilters }
      delete filters[FOLDER_FILTER_KEY]
      return filters
    },
    // Folder membership is resolved client-side, so the feed endpoint is scoped with the brand ids
    // behind the selected folders rather than the folder ids. Empty = every tracked brand.
    selectedBrandIds () {
      if (!this.selectedFolderIds.length) return []

      const brandIds = new Set()

      for (const folderId of this.selectedFolderIds) {
        const brands = folderId === DEFAULT_FOLDER_ID
          ? getUnfolderedBrands(this.getSpyderSubscriptions, this.getFolders)
          : getBrandsInFolder(this.getSpyderSubscriptions, this.getFolders.find(folder => folder.id === folderId))

        brands.forEach(brand => {
          if (brand.id) brandIds.add(brand.id)
        })
      }

      return Array.from(brandIds)
    },
    // Guards the fetch. Without tracked brands there is nothing to show, and a folder selection
    // that resolves to no brands must not fall through to an unscoped request — an empty brand id
    // list means "every tracked brand" to the endpoint.
    hasBrandsToFetch () {
      return this.selectedFolderIds.length
        ? this.selectedBrandIds.length > 0
        : this.getSpyderSubscriptions.length > 0
    }
  },
  async mounted () {
    // Check if feed callout has been dismissed
    this.showFeedCallout = !localStorage.getItem('spyderFeedCalloutDismissed')

    // Set initial filter bar styles
    if (this.$refs.filterBar) {
      this.$refs.filterBar.style.position = 'sticky'
      this.$refs.filterBar.style.top = '16px'
      this.$refs.filterBar.style.zIndex = '40000'
    }

    // Wait for spyder subscriptions to load if needed
    if (this.getSpyderSubscriptionsLoading) {
      await this.fetchSpyderSubscriptions()
    }

    // Fetch ads from all tracked brands
    if (this.hasBrandsToFetch) {
      await this.fetchAdvertisements()
    } else {
      this.loadingAds = false
    }
  },
  methods: {
    ...mapActions('SpyderModule', ['fetchSpyderSubscriptions']),
    async fetchAdvertisements (load = true) {
      if (!this.hasBrandsToFetch) {
        this.advertisements = []
        this.loadingAds = false
        return
      }

      this.loadingAds = load
      this.advertisements = []

      await clearUniqueCreatives()

      const ranBetweenStart = +new Date(this.selectedDateRange?.start) || null
      const ranBetweenEnd = +new Date(this.selectedDateRange?.end) || null

      try {
        const spyderFeedQuery = {
          ...this.apiFilters,
          ranBetweenStart,
          ranBetweenEnd,
          textSearch: this.textSearch,
          sort: this.sortOrder[0].value
        }
        const { data: results, nextPage } = await ForeplayAPI.Ads.getSpyderFeedAds(null, spyderFeedQuery, this.selectedBrandIds)

        this.lastDocId = nextPage

        if (this.getUniqueCreatives) {
          const uniqueCreatives = await checkAndCacheAll(results)
          this.advertisements = uniqueCreatives
        } else {
          this.advertisements = results
        }
      } catch (e) {
        console.error(e)
        const errType = e.response?.data?.error || 'unknown'
        if (errType === 'user_error') {
          this.$showAlert({ type: 'error', message: e.response?.data?.message })
        }
      } finally {
        this.loadingAds = false
        this.infiniteId += 1
      }
    },
    async getMoreAds ($state) {
      if (!this.lastDocId) {
        if (Object.keys($state).length) {
          $state.complete()
        }
        return
      }

      const ranBetweenStart = +new Date(this.selectedDateRange.start) || null
      const ranBetweenEnd = +new Date(this.selectedDateRange.end) || null

      const spyderFeedQuery = {
        ...this.apiFilters,
        ranBetweenStart,
        ranBetweenEnd,
        textSearch: this.textSearch,
        sort: this.sortOrder[0].value
      }
      const { data: results, nextPage } = await ForeplayAPI.Ads.getSpyderFeedAds(
        this.lastDocId,
        spyderFeedQuery,
        this.selectedBrandIds
      )

      this.lastDocId = nextPage

      if (!this.lastDocId) {
        if (Object.keys($state).length) {
          $state.complete()
        }
        return
      }

      if (this.getUniqueCreatives) {
        const uniqueCreatives = await checkAndCacheAll(results)
        this.advertisements = [...this.advertisements, ...uniqueCreatives]
      } else {
        this.advertisements = [...this.advertisements, ...results]
      }

      if (Object.keys($state).length) {
        $state.loaded()
      }
    },
    updateAdsLocal (adsToUpdate) {
      const duplicateAds = [...this.advertisements]
      adsToUpdate.forEach((ad) => {
        const index = duplicateAds.findIndex((a) => a.id === ad.id)
        if (index !== -1) {
          duplicateAds[index] = ad
        }
      })
      this.advertisements = duplicateAds
    },
    clearFilters () {
      this.activeFilters = {}
      this.selectedDateRange = { start: null, end: null }
      this.textSearch = ''
      this.sortOrder = [{ name: 'Newest', value: 'desc' }]
      this.fetchAdvertisements()
    },
    dismissFeedCallout () {
      this.showFeedCallout = false
      localStorage.setItem('spyderFeedCalloutDismissed', 'true')
    },
    onScroll (e, position) {
      if (!this.$refs.filterBar) return

      const marginValue = position.scrollTop / 6 < 16 ? position.scrollTop / 6 : 16
      const rounding = position.scrollTop / 10 < 5 ? position.scrollTop / 10 : 5

      this.$refs.filterBar.style.marginLeft = `${marginValue}px`
      this.$refs.filterBar.style.marginRight = `${marginValue}px`
      this.$refs.filterBar.style.marginTop = `${marginValue}px`
      this.$refs.filterBar.style.borderRadius = `${rounding}px`

      const shadowSection = (maxValue) => {
        return position.scrollTop / 15 < maxValue ? position.scrollTop / 15 : maxValue
      }

      if (position.scrollTop) {
        this.$refs.filterBar.style.boxShadow = `rgba(0, 0, 0, 0.08) 0px ${shadowSection(15)}px ${shadowSection(55)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(7)}px ${shadowSection(10)}px, rgba(0, 0, 0, 0.08) 0px 4px 6px, rgba(0, 0, 0, 0.08) 0px ${shadowSection(7)}px ${shadowSection(13)}px, rgba(0, 0, 0, 0.08) 0px -${shadowSection(3)}px ${shadowSection(5)}px`
      } else {
        this.$refs.filterBar.style.boxShadow = null
      }
    }
  },
  metaInfo: {
    title: 'Foreplay.co | Spyder Feed'
  }
}
</script>

<style scoped>
/* Tab Styles */
.spyder-view-tab {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #5E6678; /* text-text-normal */
  background-color: transparent;
  cursor: pointer;
  user-select: none;
  transition: color 150ms ease-in-out, background-color 150ms ease-in-out;
  white-space: nowrap;
}
.spyder-view-tab:hover {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
}
.spyder-view-tab.active {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
  font-weight: 500;
}

/* Feed Callout Styles */
.feed-callout {
  background-color: #FFFFFF;
  border: 1px solid #ECEFF3; /* border-border-normal */
}

/* Search Styles */
.search-container {
  position: relative;
  box-shadow: 0px 1px 2px 0px rgba(32, 36, 50, 0.08), 0px 0px 0px 1px #EBEFF3;
  background: linear-gradient(90deg, rgba(76, 76, 82, var(--gradient-opacity)) 0%, rgba(52, 52, 59, var(--gradient-opacity)) 100%);
  transition: box-shadow 100ms ease-in-out, --gradient-opacity 100ms ease-in-out;
  width: 100%;
}
.search-input {
  color: black;
  transition: color 100ms ease-in-out;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  padding: 0;
  margin: 0;
  border: none;
  color: inherit;
  background-color: transparent;
  outline: none;
  font-feature-settings: 'ss01' on, 'cv10' on, 'liga' off, 'calt' off;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}
.search-input:focus {
  outline: none;
  border: none;
  box-shadow: none;
}
.search-input::placeholder {
  color: #5E6678; /* text-text-normal */
  transition: color 100ms ease-in-out;
}
.search-input-container:hover .search-input::placeholder {
  color: #303546; /* text-text-muted */
}
.search-input-container:hover, .search-input-container:focus-within {
  box-shadow: 0px 1px 2px 0px rgba(32, 36, 50, 0.08), 0px 0px 0px 1px #808899;
}
</style>

